/**
 * Admin Auth Guard Component - Better Auth Protection
 *
 * PROJECT_GUIDE.md uyumlu admin route protection component.
 * Better Auth session validation ile admin-only access control.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { adminSessionUtils } from '@/lib/auth-client';

/**
 * Admin Role Context
 *
 * Provides canWrite flag to all admin panel components.
 * - admin: canWrite = true (full access)
 * - viewer: canWrite = false (read-only)
 */
interface AdminRoleContextValue {
  canWrite: boolean;
  role: string | null;
}

const AdminRoleContext = createContext<AdminRoleContextValue>({ canWrite: true, role: null });

export function AdminRoleProvider({ children, canWrite, role }: { children: React.ReactNode; canWrite: boolean; role: string | null }) {
  return (
    <AdminRoleContext.Provider value={{ canWrite, role }}>
      {children}
    </AdminRoleContext.Provider>
  );
}

/** Hook to check if the current admin user has write access */
export function useCanWrite(): boolean {
  return useContext(AdminRoleContext).canWrite;
}

/** Hook to get the current admin user's role */
export function useAdminRole(): string | null {
  return useContext(AdminRoleContext).role;
}

interface AdminAuthGuardProps {
  children: React.ReactNode;
  /**
   * Redirect path if not authenticated
   */
  redirectTo?: string;
  /**
   * Loading component to show during auth check
   */
  loadingComponent?: React.ReactNode;
  /**
   * Unauthorized component to show if not admin
   */
  unauthorizedComponent?: React.ReactNode;
}

/**
 * Auth Guard States
 */
type AuthState = 'loading' | 'authenticated' | 'unauthenticated' | 'unauthorized';

/**
 * Admin Auth Guard Component
 *
 * Protects admin routes by verifying Better Auth session and admin role.
 * Shows appropriate loading/error states during verification.
 */
export function AdminAuthGuard({
  children,
  redirectTo = '/login',
  loadingComponent,
  unauthorizedComponent
}: AdminAuthGuardProps) {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const navigate = useNavigate();

  /**
   * Check admin authentication and permissions
   */
  const checkAdminAuth = useCallback(async () => {
    try {
      // Use admin session utilities for authentication check
      const isAuth = await adminSessionUtils.isAuthenticated();

      if (!isAuth) {
        setAuthState('unauthenticated');
        navigate({ to: redirectTo });
        return;
      }

      // Verify admin role
      const isAdmin = await adminSessionUtils.verifyAdminRole();

      if (!isAdmin) {
        setAuthState('unauthorized');
        return;
      }

      setAuthState('authenticated');

    } catch (error) {
      console.error('Admin auth check failed:', error);
      setAuthState('unauthenticated');
      navigate({ to: redirectTo });
    }
  }, [navigate, redirectTo]);

  useEffect(() => {
    checkAdminAuth();
  }, [checkAdminAuth]);

  /**
   * Default loading component
   */
  const defaultLoadingComponent = (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span className="ml-2 text-gray-600">Verifying admin access...</span>
    </div>
  );

  /**
   * Default unauthorized component
   */
  const defaultUnauthorizedComponent = (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="text-6xl mb-4">🚫</div>
        <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-4">You don&apos;t have admin permissions to access this page.</p>
        <button
          onClick={() => navigate({ to: '/' })}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Go Home
        </button>
      </div>
    </div>
  );

  // Render based on auth state
  switch (authState) {
    case 'loading':
      return <>{loadingComponent || defaultLoadingComponent}</>;

    case 'authenticated':
      return <>{children}</>;

    case 'unauthorized':
      return <>{unauthorizedComponent || defaultUnauthorizedComponent}</>;

    case 'unauthenticated':
      // Will redirect, but show loading in the meantime
      return <>{loadingComponent || defaultLoadingComponent}</>;

    default:
      return <>{loadingComponent || defaultLoadingComponent}</>;
  }
}

/**
 * Hook for admin auth state
 */
export function useAdminAuth() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [canWrite, setCanWrite] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  async function checkAdminStatus() {
    try {
      setIsLoading(true);
      const [adminStatus, writeStatus] = await Promise.all([
        adminSessionUtils.verifyAdminRole(),
        adminSessionUtils.canWrite(),
      ]);
      setIsAdmin(adminStatus);
      setCanWrite(writeStatus);
    } catch (error) {
      console.error('Admin status check failed:', error);
      setIsAdmin(false);
      setCanWrite(false);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    isAdmin,
    canWrite,
    isLoading,
    refreshAdminStatus: checkAdminStatus
  };
}

/**
 * Convenience wrapper for pages that require admin access
 */
export function withAdminAuth<T extends object>(
  Component: React.ComponentType<T>,
  options?: {
    redirectTo?: string;
    loadingComponent?: React.ReactNode;
    unauthorizedComponent?: React.ReactNode;
  }
) {
  return function AdminProtectedComponent(props: T) {
    return (
      <AdminAuthGuard {...options}>
        <Component {...props} />
      </AdminAuthGuard>
    );
  };
}
